import React, {useEffect, useRef, useState} from 'react';
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Label,
    ReferenceLine,
    ResponsiveContainer
} from 'recharts';
import {ChartLineColor} from "./Styles";
import Card from "@common/Card";
import {TEST_GROUP_TEST_TYPES} from "../tests/teacher/Diagnostics/Reports/TestGroupReports";
import {calculateDistance} from "./Utils";

// const data = [
//     { x: 51, y: 20, label:'ZR'},
//     { x: 22, y: -15, label:'AR' },
//     { x: 40, y: 10, label:'NM' },
// ];

const NEARBY_THRESHOLD = 2,
    DOT_RADIUS = 12;

const ScatterPlot = (props) => {
    const {
        data = [],
        customTicksX = [],
        customTicksY = [],
        xLabel = 'x-label', yLabel = 'y-label',
        dotColor = ChartLineColor.POST_TEST,
        dotColor2 = ChartLineColor.PRE_TEST,
        lineXVisible = true,
        lineYVisible = true,
        referenceLineXValue,
        referenceLineYValue,
        referenceLineXColor,
        referenceLineYColor,
        xAxisRange= [0,100],
        xTickGap = 10,
        yAxisRange= [-100,100],
        yTickGap = 10,
        xAxisMiddle = false,
        xAxisShowFirstVal = false,
        yAxisShowFirstVal = false,
        customLabelsX = [],
        labelDisplayMap = {},
        splitData = false
    } = props;

    const [data1,setData1] = useState([]),
        [data2, setData2] = useState([]),
        [activePoint, setActivePoint] = useState(null);

    for(let i = (xAxisShowFirstVal ? 0 : 1); i < (xAxisRange[1] - xAxisRange[0])/xTickGap+1; i++){
        customTicksX.push(xAxisRange[0]+(i*xTickGap));
    }

    for(let i = (yAxisShowFirstVal ? 0 : 1); i < (yAxisRange[1] - yAxisRange[0])/yTickGap+1; i++){
        customTicksY.push(yAxisRange[0]+(i*yTickGap));
    }

    const handleMouseMove = (event) => {
        // console.log('event: ',event);
        if (event !== null) {
            // console.log('{x:event.chartX, y:event.chartY}: ',{x:event.chartX, y:event.chartY});
            setActivePoint({x:event.node.x, y:event.node.y});
        } else {
            setActivePoint(null);
        }
    };

    const handleMouseOut = (event) =>{
        setActivePoint(null);
    }

    useEffect(()=>{
        if(data.length > 0){
            if(splitData){
                const postTestData = [],
                    preTestData = [];
                data.forEach((dataPoint)=>{
                    const timeTaken = Math.round(dataPoint.timeTaken/60),
                        row = {...dataPoint, id:dataPoint.userId+'-'+dataPoint.testType, key:dataPoint.userId+'-'+dataPoint.testType, timeTakenMins:timeTaken, timeTakenSeconds:dataPoint.timeTaken%60, x:timeTaken, y:dataPoint.value};
                    if(dataPoint.testType === TEST_GROUP_TEST_TYPES.POST_TEST){
                        row.testTypeDisplay = 'post-test';
                        postTestData.push(row);
                    }else{
                        row.testTypeDisplay = 'pre/mid-test';
                        preTestData.push(row);
                    }
                });
                setData1(postTestData);
                setData2(preTestData);
                // console.log('data1.map: ',data1.map(({ key }) => key));
                // console.log('data2.map: ',data2.map(({ key }) => key));

            }else{
                setData1(data);
            }
        }
    },[data]);

    const CustomDot = (props) => {
        const { cx, cy, stroke, color , payload, value } = props;

        // console.log('payload: ',payload);
        // console.log('activePoint: ',activePoint);
        // console.log('payload: ',payload);
        // console.log('props: ',props);

        if(activePoint !== null && calculateDistance(activePoint.x,activePoint.y,payload.x,payload.y) <= NEARBY_THRESHOLD){
            return (
                <g onMouseLeave={handleMouseOut}>
                    <circle cx={cx} cy={cy} r={DOT_RADIUS-1} fill={'#ffffff'} stroke={color} strokeWidth={1} />
                    <circle cx={cx} cy={cy} r={DOT_RADIUS-3} fill={color} />
                    <text x={cx} y={cy+4} textAnchor="middle" fontSize="11px" fill="white">
                        {payload.label}
                    </text>
                </g>
            )
        }

        return (
            // <svg style={{ pointerEvents: 'all' }} x={cx - DOT_RADIUS} y={cy - DOT_RADIUS} width={DOT_RADIUS*2} height={DOT_RADIUS*2} viewBox={"0 0  "+DOT_RADIUS*2 + " "+DOT_RADIUS*2}>
            //     {/*<circle cx={DOT_RADIUS} cy={DOT_RADIUS} r={DOT_RADIUS} fill={color} />*/}
            //     <text x={cx} y={cy - 10} textAnchor="middle" fontSize="12px" fill="red">
            //         {payload.label}
            //     </text>
            // </svg>

            //changed to <g> element so that the text and circle are rendered at the same time unlike the <LabelList/> Component
            <g>
                <circle cx={cx} cy={cy} r={DOT_RADIUS} fill={color} />
                <text x={cx} y={cy+4} textAnchor="middle" fontSize="11px" fill="white">
                    {payload.label}
                </text>
            </g>
        );
    };

    const CustomActiveDot = (props) => {
        // console.log('props: ',props);
        const { cx, cy, stroke, color , payload, value } = props;

        return (
            <g x={cx - DOT_RADIUS} y={cy - DOT_RADIUS} width={DOT_RADIUS*2} height={DOT_RADIUS*2} viewBox={"0 0  "+DOT_RADIUS*2 + " "+DOT_RADIUS*2}>
                <circle cx={DOT_RADIUS} cy={DOT_RADIUS} r={DOT_RADIUS-1} fill={'#ffffff'} stroke={color} strokeWidth={1} />
                <circle cx={DOT_RADIUS} cy={DOT_RADIUS} r={DOT_RADIUS-3} fill={color} />
            </g>
        );
    };

    const CustomTooltip = ({ active, payload, label }) => {
        // console.log('payload: ',payload);
        if (active && payload && payload.length) {

            // console.log('payload: ',payload);

            const user = payload[0].payload,
                color = user.testType === TEST_GROUP_TEST_TYPES.POST_TEST ? dotColor : dotColor2,
                mapLength = Object.values(labelDisplayMap).length;

            let displayStr = '';

            // console.log('user: ',user);

            const displayUsers = [];

            data1.forEach((row)=>{
                if(calculateDistance(user.x,user.y,row.x,row.y) <= NEARBY_THRESHOLD){
                    displayUsers.push(row);
                }
            })

            data2.forEach((row)=>{
                if(calculateDistance(user.x,user.y,row.x,row.y) <= NEARBY_THRESHOLD){
                    displayUsers.push(row);
                }
            })

            // console.log('displayUsers: ',displayUsers);

            if(mapLength > 0){
                displayUsers.forEach((user)=>{
                    if(splitData){
                        displayStr = user.name + ' '+ user.testTypeDisplay + ' ( ';
                    }else{
                        displayStr = user.name + ' ( ';
                    }
                    let count = 0;
                    if(user.answered){
                        for(const [key,value] of Object.entries(labelDisplayMap)){
                            if(user.hasOwnProperty(String(key))){
                                if(value[0] === '%'){
                                    displayStr += user[String(key)]+value;
                                }else{
                                    displayStr += user[String(key)]+' '+value;
                                }
                            }else{
                                console.warn('missing key \''+key+'\' in data for tooltip');
                            }
                            if(count === mapLength-1){
                                displayStr += ' )';
                            }else{
                                displayStr += ', '
                            }
                            count++;
                        }
                    }else{
                        displayStr += 'unanswered )';
                    }
                    user.displayStr = displayStr;
                });
            }

            displayStr = displayStr.replaceAll('-','−');

            // (user.name +' ('+ user.value +'% '+'correct, '+user.difference+'% growth)')
            return (
                <Card className="ch-card-rounded" style={{marginLeft:'4px', marginTop:'-30px', borderColor:color, background:'white'}}>
                    {displayUsers.map((user)=>{
                        return(<p key={user.userId+''+user.testType}>{user.displayStr}</p>);
                    })}
                </Card>
            );
        }
        return null;
    };

    return (
        <ResponsiveContainer width="96%" height={400}>
            <ScatterChart width={800} height={400}
                          margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                          >
                <CartesianGrid horizontal={true} vertical={false} />
                <XAxis type="number" dataKey="x" domain={xAxisRange} ticks={customTicksX} axisLine={{strokeWidth: 0}} tickLine={{stroke: '#ccc'}} >
                    <Label position="insideBottom" value={xLabel} dy={(xAxisMiddle ? 12 : 24)} />
                </XAxis>
                <YAxis type="number" dataKey="y" domain={yAxisRange} ticks={customTicksY} strokeWidth={0} >
                    <Label value={yLabel} offset={5} position="insideLeft"
                           angle={-90}
                           dx={0} dy={65}
                           overflow={'visible'} />
                </YAxis>
                {/*<XAxis dataKey="x"/>*/}
                {/*<ReferenceLine y={0} stroke="red" label={<CustomTick x={ScatterChart.defaultProps.width} y={200} value={'50'} />} />*/}
                {lineYVisible && <ReferenceLine x={referenceLineXValue} stroke={referenceLineYColor} strokeWidth={3} strokeDasharray="18 4" />}
                {lineXVisible && <ReferenceLine y={referenceLineYValue} stroke={referenceLineXColor} strokeWidth={3} strokeDasharray="18 4" />}
                <Tooltip content={<CustomTooltip />} cursor={{strokeDasharray:"1 3"}}  />
                {(data1 && data1.length > 0) &&
                    <Scatter
                        onMouseMove={(e)=>{handleMouseMove(e)}} onMouseLeave={handleMouseOut}
                        data={data1}
                        shape={<CustomDot color={dotColor} />}

                         // activeShape={<CustomActiveDot color={dotColor}/>
                        >
                        {/*<LabelList dataKey="label" position="center" fill={'#ffffff'} style={{ pointerEvents: 'none' }} fontSize={11}/>*/}
                    </Scatter>
                }
                {(data2 && data2.length > 0) &&
                    <Scatter
                        onMouseMove={(e)=>{handleMouseMove(e)}}
                        onMouseLeave={handleMouseOut}
                        data={data2}
                        shape={<CustomDot color={dotColor2}/>}
                         // activeShape={<CustomActiveDot color={dotColor2}/>}
                    >
                        {/*<LabelList dataKey="label" position="center" fill={'#ffffff'} style={{ pointerEvents: 'none' }} fontSize={11}/>*/}
                    </Scatter>
                }
            </ScatterChart>
        </ResponsiveContainer>
    );
}
export default ScatterPlot;